/**
 * Janela de horário de envio (M8 / CU-17tm466nzee) — contrato de
 * `GET /send-windows` e as funções puras que `app-window-datetime-picker` e
 * `app-send-when` usam para desenhar o seletor de data+hora sem hardcodar
 * os limites. Tudo em horário de Brasília (America/Sao_Paulo), independente
 * do fuso do navegador.
 *
 * O fim é sempre EXCLUSIVO aqui (`[start, stop)`), nos dois produtos — para
 * SMS é uma margem segura: o backend ainda aceita `22:00:00` em ponto
 * (`AppHelper::broadcastCanSend`, limite inclusivo e precisão de segundo),
 * mas "22:00" aparece bloqueado no campo de hora, como no mockup aprovado. Para
 * ligação é exato (`CallSendWindow::isBlocked`, fim exclusivo e precisão de
 * minuto).
 */

export const SEND_WINDOW_TIMEZONE = 'America/Sao_Paulo';

export interface SendWindow {
  /** 'HH:mm', 24h, horário de Brasília. */
  start: string;
  /** 'HH:mm', 24h, horário de Brasília. Fim EXCLUSIVO. */
  stop: string;
}

export interface SendWindows {
  timezone: string;
  /** ISO 8601 com offset de São Paulo — hora do servidor no momento da resposta. */
  now: string;
  sms: SendWindow;
  call: SendWindow;
}

/**
 * O mesmo que o backend aplicaria se `GET /send-windows` nunca respondesse
 * (rede fora, stub de teste devolvendo `{}` — `cypress/support/carteira-stub.js`
 * intercepta `**\/api/**` com corpo vazio). O backend continua sendo a trava
 * real (400 do SMS, ajuste de horário da ligação); isto é só para a UI não
 * ficar sem limites no campo de hora.
 */
export const DEFAULT_SEND_WINDOWS: SendWindows = {
  timezone: SEND_WINDOW_TIMEZONE,
  now: new Date().toISOString(),
  sms: { start: '08:00', stop: '22:00' },
  call: { start: '07:30', stop: '23:00' },
};

/** 'HH:mm' -> minutos desde a meia-noite. '07:30' -> 450. Entrada inválida -> 0. */
export function minutesOf(hhmm: string): number {
  const [hours, minutes] = (hhmm || '0:0').split(':').map(part => parseInt(part, 10) || 0);
  return hours * 60 + minutes;
}

/** Minutos desde a meia-noite -> 'HH:mm'. */
export function hhmmOf(totalMinutes: number): string {
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Partes do relógio de Brasília para um instante absoluto, via
 * `Intl.DateTimeFormat` — sem lib nova (nem `date-fns-tz` nem
 * `moment-timezone` estão no `package.json`).
 */
export interface SpParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

export function spParts(moment: Date = new Date()): SpParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SEND_WINDOW_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(moment);

  const get = (type: string): number => parseInt(parts.find(p => p.type === type)?.value ?? '0', 10);

  // Algumas implementações de ICU devolvem "24" para meia-noite com
  // hour12: false; '24:15' viraria inválido no campo de hora.
  const hour = get('hour') % 24;

  return { year: get('year'), month: get('month'), day: get('day'), hour, minute: get('minute') };
}

/** 'YYYY-MM-DD' do dia de hoje em Brasília. */
export function spToday(now: Date = new Date()): string {
  const { year, month, day } = spParts(now);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Minutos desde a meia-noite, em Brasília, para um instante absoluto. */
export function spMinutesOfDay(moment: Date = new Date()): number {
  const { hour, minute } = spParts(moment);
  return hour * 60 + minute;
}

/**
 * Constrói o instante absoluto correto para uma data+hora de PAREDE em
 * Brasília — não depende do fuso do navegador. Brasil não observa horário
 * de verão desde 2019, então o offset de São Paulo é sempre fixo (-03:00).
 *
 * Isto é o que o `save()` do broadcast deve passar para o backend: o
 * `Date.prototype.toJSON` global (`app.module.ts`) serializa qualquer Date
 * como a hora de parede de São Paulo (`toLocaleString('en-US',{timeZone:SP})`),
 * então o instante absoluto certo aqui garante que o texto serializado seja
 * exatamente `dateYmd`/`timeHm`, em qualquer fuso de navegador.
 */
export function spWallTimeToDate(dateYmd: string, timeHm: string): Date {
  return new Date(`${dateYmd}T${timeHm}:00-03:00`);
}

/** O instante (ou agora) cai dentro da janela `[start, stop)` de Brasília? */
export function isInWindow(window: SendWindow, moment: Date = new Date()): boolean {
  const current = spMinutesOfDay(moment);
  return current >= minutesOf(window.start) && current < minutesOf(window.stop);
}
